import { Archive, ClipboardCheck, FileText, House, Images, MonitorPlay, UserRound } from 'lucide-react';
import { STAFF_NAV } from '../ui/Sidebar';

// Navigation by audience (DESIGN rev 2.1 §3.1, §3.3).

// HD-9 0c: the API's role gates, mirrored so no page calls a route that refuses its viewer.
// /api/talent-portal/* needs the Talent role (routes/talentPortal.js); a staff account without it has no talent pages.
export const hasTalentRole = user => (user?.roles || []).includes('Talent');
// Talent-only = every role held is Talent (the API's requireStaff, SEC-1). Staff = signed in, not an external client account,
// and not talent-only (the /ui primitives sheet is for the team).
export const isTalentOnly = user => { const roles = user?.roles || []; return roles.length > 0 && roles.every(r => r === 'Talent'); };
export const isStaff = user => !!user && !user.isExternal && !isTalentOnly(user);
// SEC-1: the pages a talent-only account may open inside the app; every other logged-in route sends them to /talent.
// HD-11.1 (P2): the logged-in paths App.jsx mounts inside PrivateRoute. Any other path is "Page not found" in the viewer's
// own frame (not a bounce to Home, which hid the mistake). Keep in step with App.jsx: tests/appPaths.test.mjs checks it.
// React Router matches case-insensitively and ignores a trailing slash, so this does too.
export const APP_PATHS = ['/', '/dashboard', '/scripts', '/qual', '/live', '/calendar', '/edit-queue', '/scheduler', '/reports', '/assistant',
  '/admin', '/account', '/time-off', '/assets', '/footage', '/footage/ingest', '/storage', '/talent', '/talent/availability', '/talent/scripts'];
export function isAppPath(pathname, { aiEnabled = false } = {}) {
  const p = (pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname).toLowerCase();
  return APP_PATHS.includes(p) || /^\/production\/[^/]+$/.test(p) || (aiEnabled && p === '/ai');
}

export const talentMayOpen = pathname => pathname === '/talent' || pathname.startsWith('/talent/') || pathname === '/account';
// SEC-2: the pages a client (external) account may open inside the app: its rail (CLIENT_NAV), its account, and its own
// talent pages if it also holds Talent (TalentOnly decides). Every other logged-in route sends it to Home; the API
// answers 404 to a client on those routes.
// /live (read-only for clients) and /production/:id (its "not available" state, no call) open from Home's rows.
export const CLIENT_PATHS = ['/dashboard', '/scripts', '/qual', '/storage', '/assets', '/account', '/live'];
export const clientMayOpen = pathname => CLIENT_PATHS.includes(pathname) || pathname.startsWith('/production/')
  || pathname === '/talent' || pathname.startsWith('/talent/');

// Staff: the four real groups. Raw footage is internal (the API refuses external users on every footage route);
// AI production follows the deployment flag. A dual-role staff + Talent user also gets their talent portal.
// workbench: the "Workbench" item for the production open right now (workbenchItem below), placed after Live production.
export function staffNav({ isExternal = false, aiEnabled = false, hasTalent = false, workbench = null } = {}) {
  const groups = STAFF_NAV
    .map(g => ({ ...g, items: g.items.filter(i => (!i.internalOnly || !isExternal) && (!i.flag || (i.flag === 'ai' && aiEnabled))) }))
    .map(g => (workbench && g.items.some(i => i.to === '/live')
      ? { ...g, items: g.items.flatMap(i => (i.to === '/live' ? [i, workbench] : [i])) }
      : g))
    .filter(g => g.items.length);
  return hasTalent ? [...groups, { group: 'Talent', items: [{ to: '/talent', label: 'My talent portal', icon: UserRound }] }] : groups;
}

// Logged-in external client users: the icon rail with the client set, Home · Scripts · Review · Storage · Assets.
// Review is the Qual page (where a client reviews cuts); Home is the dashboard they land on today.
export const CLIENT_NAV = [{ group: 'Client', items: [
  { to: '/dashboard', label: 'Home', icon: House },
  { to: '/scripts', label: 'Scripts', icon: FileText },
  { to: '/qual', label: 'Review', icon: ClipboardCheck },
  { to: '/storage', label: 'Storage', icon: Archive },
  { to: '/assets', label: 'Assets', icon: Images },
] }];

// Workbench routes: the sidebar is a rail, always, the tier is dark and there is no theme toggle (§3.2).
const PRODUCTION_PATH = /^\/production\/([^/]+)\/?$/;
export const isWorkbench = path => PRODUCTION_PATH.test(path) || path === '/footage/ingest';

// HD-8 (Owner ruling, the two-click Workbench): while a production is open, the rail carries a "Workbench" item that opens
// it in its editing mode: the Edit stage with the scrub dock open. It is a link (href) and reads as current in that mode.
export function workbenchItem(pathname, search = '') {
  const m = PRODUCTION_PATH.exec(pathname || '');
  if (!m) return null;
  const to = `/production/${m[1]}?stage=edit&dock=open`;
  const q = new URLSearchParams(search);
  return { to, href: to, label: 'Workbench', icon: MonitorPlay, current: q.get('stage') === 'edit' && q.get('dock') === 'open' };
}

// Routes whose page has migrated to the shell's PageHeader (HD-4: Dashboard only, as the proof).
export const MIGRATED = new Set(['/dashboard', '/account', '/time-off', '/assistant', '/reports', '/scheduler', '/edit-queue', '/live', '/ai', '/qual',
  '/admin', '/storage', '/footage', '/calendar',   // HD-6
  '/scripts',      // HD-7
  '/footage/ingest',   // HD-8 (and every /production/:id, see isMigrated)
  '/talent', '/talent/availability', '/talent/scripts', '/assets']);   // HD-9: a staff + Talent user reads them in the rail shell

// A route whose page renders inside the shell's PageHeader. /production/:id is a pattern, so it is matched, not listed.
export const isMigrated = path => MIGRATED.has(path) || PRODUCTION_PATH.test(path);
